import React from 'react';

/**
 * Minimal Markdown renderer for chat bubbles.
 *
 * The assistant replies in Markdown, which used to show as literal `**` and `-`
 * characters. This renders the small subset the bot actually uses (paragraphs,
 * bullet and numbered lists, bold, inline code, links and bare URLs) as real
 * elements. No HTML is interpreted, so nothing from the model can inject markup.
 */

const LINK = /\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?])|\*\*([^*]+)\*\*|`([^`]+)`/g;

const linkClass = 'font-medium text-[#8fd13f] underline decoration-[#8fd13f]/40 underline-offset-2 hover:decoration-[#8fd13f] break-words';

/** Long bare URLs read badly in a narrow bubble, so label the ones we recognise. */
function labelForUrl(url: string): string {
  try {
    const { pathname } = new URL(url);
    const product = pathname.match(/^\/products\/([^/]+)/);
    if (product) return `View ${decodeURIComponent(product[1])}`;
    if (pathname.startsWith('/clothing')) return 'Browse garments';
    if (pathname.startsWith('/printing')) return 'Browse print products';
    if (pathname.startsWith('/services')) return 'See this service';
    if (pathname === '/' || pathname === '') return 'Our website';
    const pretty = (url.split('://')[1] || url).replace(/^www\./, '');
    return pretty.length > 44 ? `${pretty.slice(0, 41)}…` : pretty;
  } catch {
    return url;
  }
}

function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  LINK.lastIndex = 0;

  while ((match = LINK.exec(text)) !== null) {
    if (match.index > last) out.push(text.slice(last, match.index));
    const key = `${keyPrefix}-${match.index}`;
    const [, label, href, bareUrl, bold, code] = match;

    if (label && href) {
      out.push(<a key={key} href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>{label}</a>);
    } else if (bareUrl) {
      out.push(<a key={key} href={bareUrl} target="_blank" rel="noopener noreferrer" className={linkClass}>{labelForUrl(bareUrl)}</a>);
    } else if (bold) {
      out.push(<strong key={key} className="font-semibold text-white">{bold}</strong>);
    } else if (code) {
      out.push(<code key={key} className="px-1 py-0.5 rounded bg-black/25 text-[0.92em]">{code}</code>);
    }
    last = match.index + match[0].length;
  }

  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] };

function toBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  // Treat a bullet that follows text on the same line as its own line.
  const lines = source
    .replace(/\r/g, '')
    .replace(/([^\n])\s+(?=[-*]\s|\d+\.\s)/g, '$1\n')
    .split('\n');

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (blocks.length && blocks[blocks.length - 1].kind === 'p') blocks.push({ kind: 'p', lines: [] });
      continue;
    }

    const bullet = line.match(/^[-*]\s+(.*)$/);
    const numbered = line.match(/^\d+[.)]\s+(.*)$/);
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    const prev = blocks[blocks.length - 1];

    if (bullet) {
      if (prev && prev.kind === 'ul') prev.items.push(bullet[1]);
      else blocks.push({ kind: 'ul', items: [bullet[1]] });
    } else if (numbered) {
      if (prev && prev.kind === 'ol') prev.items.push(numbered[1]);
      else blocks.push({ kind: 'ol', items: [numbered[1]] });
    } else if (heading) {
      blocks.push({ kind: 'p', lines: [`**${heading[1]}**`] });
    } else if (prev && (prev.kind === 'ul' || prev.kind === 'ol') && prev.items.length) {
      // A plain line straight after a list item continues it, e.g. a link under a product.
      prev.items[prev.items.length - 1] += `\n${line}`;
    } else if (prev && prev.kind === 'p' && prev.lines.length) {
      prev.lines.push(line);
    } else {
      blocks.push({ kind: 'p', lines: [line] });
    }
  }

  return blocks.filter((b) => (b.kind === 'p' ? b.lines.length > 0 : b.items.length > 0));
}

const ChatMarkdown: React.FC<{ text: string }> = ({ text }) => {
  const blocks = toBlocks(text || '');

  return (
    <div className="space-y-2 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
      {blocks.map((block, i) => {
        if (block.kind === 'p') {
          return (
            <p key={i} className="leading-[1.5]">
              {block.lines.map((line, j) => (
                <React.Fragment key={j}>
                  {j > 0 && <br />}
                  {renderInline(line, `${i}-${j}`)}
                </React.Fragment>
              ))}
            </p>
          );
        }

        const ordered = block.kind === 'ol';
        const List = ordered ? 'ol' : 'ul';
        return (
          <List key={i} className="space-y-1.5">
            {block.items.map((item, j) => (
              <li key={j} className="flex gap-2 leading-[1.5]">
                {ordered ? (
                  <span aria-hidden className="shrink-0 tabular-nums text-white/50">{j + 1}.</span>
                ) : (
                  <span aria-hidden className="shrink-0 mt-[0.45em] w-1.5 h-1.5 rounded-full bg-[#8fd13f]" />
                )}
                <span className="min-w-0">
                  {item.split('\n').map((line, k) => (
                    <React.Fragment key={k}>
                      {k > 0 && <br />}
                      {renderInline(line, `${i}-${j}-${k}`)}
                    </React.Fragment>
                  ))}
                </span>
              </li>
            ))}
          </List>
        );
      })}
    </div>
  );
};

export default ChatMarkdown;
